/**
 * edgeWaypoints — pure geometry for user-routed transition edges (ENG-1061).
 *
 * A State Machine transition can carry `data.waypoints`: an ordered list of
 * `{x, y}` points (in ReactFlow flow coordinates) the wire is routed THROUGH,
 * Unreal-reroute-node style. Double-clicking a transition drops one; dragging
 * it reshapes the curve; double-clicking it removes it. These helpers build the
 * SVG path and decide where a new waypoint is inserted — kept pure and separate
 * from the React edge component so they can be unit-tested without a canvas.
 *
 * All points are `{x, y}`; every path helper returns an SVG path `d` string
 * whose first command is `M` (so an empty/short input yields '').
 */

/** Straight polyline through the points: `M x0,y0 L x1,y1 …`. */
export function polylinePath(points) {
  if (!points || points.length < 2) return ''
  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 1; i < points.length; i++) d += ` L ${points[i].x},${points[i].y}`
  return d
}

/**
 * Smooth Catmull-Rom spline through the points, emitted as cubic beziers
 * (`M … C …`). The curve passes through every point; endpoints are duplicated
 * so the first/last segments don't overshoot. Guarantees a `C` command for any
 * input with ≥2 points (so callers can treat it as the "arched" shape).
 */
export function smoothSplinePath(points) {
  if (!points || points.length < 2) return ''
  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] || p2
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = p1.y + (p2.y - p0.y) / 6
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`
  }
  return d
}

/** Shortest distance from point `p` to segment `a`–`b`. */
export function distToSegment(p, a, b) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  let t = len2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return Math.hypot(p.x - cx, p.y - cy)
}

/**
 * Index into a waypoint array at which to insert a new point clicked at `p`,
 * given the FULL routed point list `pts = [source, ...waypoints, target]`.
 * Finds the segment nearest the click; a new waypoint inserted there keeps the
 * route in visual order. Returns a value in [0, waypoints.length].
 */
export function insertIndexForClick(pts, p) {
  if (!pts || pts.length < 2) return 0
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < pts.length - 1; i++) {
    const d = distToSegment(p, pts[i], pts[i + 1])
    if (d < bestD) { bestD = d; best = i }
  }
  return best // segment i sits between pts[i] and pts[i+1] → waypoint index i
}
